import React from "react";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Choose from "../chooseItems/index.jsx";

// One tile in edit mode. The outer div carries the dnd-kit transform, the
// inner div carries the wobble so the two transforms never fight.
const SortableTile = ({ product }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: product.id });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`sortable-${product.id}`}
      data-dragging={isDragging ? "true" : "false"}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 2 : undefined,
        touchAction: "none",
        cursor: isDragging ? "grabbing" : "grab",
        outline: "none",
      }}
    >
      <div className={isDragging ? undefined : "pos-tile-wobble"}>
        <Choose data={product} editing />
      </div>
    </div>
  );
};

// The product grid of the subcategory being rearranged. `onReorder` receives
// { activeId, overId } (dnd-kit ids = product ids) when a tile is dropped on
// another one.
const SortableProductGrid = ({ products, onReorder, gridStyle }) => {
  // Two input-specific sensors on purpose. A single PointerSensor with a
  // `delay` constraint cancels the drag as soon as the pointer moves more
  // than `tolerance` px during the delay — a mouse user who presses and
  // moves at once never grabs anything. So: mouse activates on distance
  // (a click never moves a tile), touch keeps a short hold so the page can
  // still scroll with a finger.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) onReorder({ activeId: active.id, overId: over.id });
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={products.map((product) => product.id)} strategy={rectSortingStrategy}>
        <div style={gridStyle}>
          {products.map((product) => (
            <SortableTile key={product.id} product={product} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableProductGrid;
